import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import SearchBox from '../components/psc/lib/SearchBox'
import StatusOptions from '../components/psc/lib/StatusOptions'
import GncDataTable from '../components/psc/lib/GncDataTable'
import Loading from '../components/psc/lib/Loading'

import { setIsLoading, setIsDeleted, clearProject } from '../obj/pscState'
import { GncLink } from '../obj/history'

import useAuthentication from '../hooks/useAuthentication'
import useUserManagement from '../hooks/useUserManagement'
import useProjectList from '../hooks/useProjectList'
import useBrowserTabTitle from '../hooks/useBrowserTabTitle'

const NewOpportunityButton = (props) => {
    return (
        <span>
            <Link to="/portfolio/new/information?project_type_id=2" type="button" className="btn btn-mr btn-primary">New Opportunity</Link>
            &nbsp;
        </span>
    )
}

const NewProjectButton = (props) => {
    return (
        <span>
            <Link to="/portfolio/new/information?project_type_id=1" type="button" className="btn btn-mr btn-primary">New Project</Link>
            &nbsp;
        </span>
    )
}

const NewProductButton = (props) => {
    return (
        <span>
            <Link to="/portfolio/new/information?project_type_id=3" type="button" className="btn btn-mr btn-primary">New Product</Link>
            &nbsp;
        </span>
    )
}

const ProjectPortfolio = () => {
    useBrowserTabTitle('Portfolio')

    const [statusFilter, setStatusFilter] = useState('HAS_ACCESS')
    const [showOnlyFavourites, setShowOnlyFavourites] = useState(false)
    
    const pscState = useSelector(state => state.pscState.value)
    // Just for checking if we just deleted a project. THIS flag will need to be deprecated asap, it's fucked up.
    const isDeleted = pscState.isDeleted
    
    const {
        getUserFromID,
        userCanCreateOpportunity,
        userCanCreateProject,
        userCanCreateProduct,
        userHasProjectAccess,
        userHasExplicitProjectAccess,
        userHasProjectInformationAccess,
        userAddFavouriteProject,
        userRemoveFavouriteProject,
        userProjectAccessLabel,
        userProjectIsFavourite,
        isUserManagementInitialized
    } = useUserManagement()

    const {
        projects,
    } = useProjectList()

    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate();
    const { currentUser } = useAuthentication()

    if( isDeleted ) {
        console.log("Just deleted a project. Making sure storage is clean")
        dispatch(clearProject())
        dispatch(setIsDeleted(false))
    }
    
    const query = new URLSearchParams(location.search)
    const projectContext = query.has('project_context') ? query.get('project_context') : 'dashboard'
    
    const table = useRef(null)
    const localStorageOrderKey = 'tblPortfolioOrder'
    const defaultOrder = [[ 0, 'asc' ]]

    const localStorageStatusesKey="portfolio-list-statuses"
    const localStorageShowOnlyFavs="portfolio-list-show-only-favourites"
    const statuses = [
        { value: 'MY_OPPORTUNITIES', label: 'My Opportunities' },
        { value: 'MY_PROJECTS', label: 'My Projects' },
        { value: 'MY_PRODUCTS', label: 'My Products' },
        { value: 'HAS_ACCESS', label: 'My Opportunities, Projects and Products' },
        { value: 'ALL_OPPORTUNITIES', label: 'All Opportunities' },
        { value: 'ALL_PROJECTS', label: 'All Projects' },
        { value: 'ALL_PRODUCTS', label: 'All Products' },
        { value: '', label: 'All Opportunities, Projects and Products' },
        { value: 'ARCHIVED', label: 'Archived', icon: 'fa-archive' },
    ]

    const columns = [
        { data: 'has_explicit_access', visible: false },
        { data: 'is_favourite' },
        { data: 'project_code', searchable: true },
        { data: 'name', searchable: true },
        { data: 'manager', searchable: true },
        { data: 'project_type_name', searchable: true },
        { data: 'my_role', searchable: true },
        { data: 'project_status_name', searchable: true },
        { data: 'is_archived', visible: false }
    ]

    const tableSlots = {
        1: (data, type, row) => {
            if( type !== 'display') {
                return data ? 1 : 0
            }
            return data ? (
                <a href="#" className="set-favourite-false"
                   onClick={(event) => removeFavouriteProject(event, row.id) }
                   data-tooltip-id="gnctooltip" data-tooltip-content="Unpin from Favourites">
                    <i className="fa fa-thumb-tack"></i>
                </a>
            )
                 : (
                     <a href="#" className="set-favourite-true"
                        onClick={(event) => addFavouriteProject(event, row.id)}
                        data-tooltip-id="gnctooltip" data-tooltip-content="Pin to Favourites">
                         <i className="fa fa-thumb-tack"></i>
                     </a>
                 )
        },
        2: (data, type, row) => {
            if( type !== 'display') {
                return data
            }
            if( row.has_access || row.has_information_access ) {
                const url = `/portfolio/${row.id}/${projectContext}`
                return GncLink(navigate, url, data, () => dispatch(setIsLoading(true)))
            } else {
                return data
            }
        },
        3: (data, type, row) => {
            if( type !== 'display') {
                return data
            }
            if( row.has_access || row.has_information_access ) {
                const url = `/portfolio/${row.id}/${projectContext}`
                const opts = {}
                if( row.description.trim() !== "" ) {
                    opts.icon = {
                        className: "fa fa-info-circle pull-right",
                        dataTip: row.description.replace(/</g, '&lt;').replace(/\n/g, '<br/>')
                    }
                }
                return GncLink(navigate, url, data, () => dispatch(setIsLoading(true)), opts)
            } else {
                return data
            }
        },
        4: (data, type, row) => {
            const userId = row.bid_manager_user_id || row.project_manager_user_id
            const manager = getUserFromID(userId)
            if( type !== 'display') {
                return manager.display_name
            }
            return (<span data-tooltip-id="gnctooltip" data-tooltip-content={manager.label}>{manager.display_name}</span>)
        },
        5: (data, type, row) => {
            if( type !== 'display') {
                return data
            }
            let value = data
            if ( row.do_allow_document_sharing ) {
                value += ' <i class="fa fa-share-alt" data-tooltip-id="gnctooltip" data-tooltip-content="Allows Document Sharing"></i>'
            }
            if ( row.project_type_name === 'Product' ) {
                value += ' <i class="fa fa-cube" data-tooltip-id="gnctooltip" data-tooltip-content="Standard Product"></i>'
            }
            return value
        },
    }
    
    const tableHead = (
        <tr>
            <th>Has Project Access</th>
            <th data-tooltip-id="gnctooltip" data-tooltip-content="Favourites" style={{width: '25px'}}>&nbsp;</th>
            <th>Code</th>
            <th>Name</th>
            <th>Manager</th>
            <th>Type</th>
            <th>My Role</th>
            <th>Status</th>
            <th>Is Archived</th>
        </tr>
    )

    const visibleTableData = useMemo(() =>
        projects
            .map(project =>
                ({
                    ...project,
                    my_role: userProjectAccessLabel(currentUser, project.id),
                    has_access: userHasProjectAccess(currentUser, project.id),
                    has_information_access: userHasProjectInformationAccess(currentUser, project.id, project.project_type_name),
                    has_explicit_access: userHasExplicitProjectAccess(currentUser, project.id),
                    is_favourite: userProjectIsFavourite(currentUser, project.id),
                })
            )
            .filter((item) => {
                const projectType = item.project_type_name;
                const isFavourite = item.is_favourite;
                const isArchived = item.is_archived;
                const hasExplicitAccess = item.has_explicit_access;
                
                const statusFilterMatches = {
                    ARCHIVED: isArchived,
                    HAS_ACCESS: hasExplicitAccess,
                    MY_PROJECTS: hasExplicitAccess && !!projectType.match(/Project/),
                    MY_OPPORTUNITIES: hasExplicitAccess && !!projectType.match(/Opportunity/),
                    MY_PRODUCTS: hasExplicitAccess && !!projectType.match(/Product/),
                    ALL_PROJECTS: !!projectType.match(/Project/),
                    ALL_PRODUCTS: !!projectType.match(/Product/),
                    ALL_OPPORTUNITIES: !!projectType.match(/Opportunity/),
                    '': true,
                };

                // If showOnlyFavourites is true, only show if item is favourite, otherwise allow it through
                const filteredByFavourite = showOnlyFavourites ? isFavourite : true;

                // If statusFilter is ARCHIVED, only show if item is archived, otherwise only show if it is not archived
                const filterByArchived = statusFilter === 'ARCHIVED' ? isArchived : !isArchived;

                // If statusFilter is a valid key in statusFilterMatches, only show if it matches
                const filteredByStatus = statusFilterMatches[statusFilter];

                // Check all filters are true in order to display an item
                return filteredByFavourite && filterByArchived && filteredByStatus;
            })
        , [projects, statusFilter, showOnlyFavourites, currentUser])
    
    useEffect(() => {
        let filter = localStorage.getItem(localStorageStatusesKey)
        if (location.search) {
            const query = new URLSearchParams(location.search)
            if( query.has('filter') ) {
                filter = query.get('filter')
            }
        }
        if( filter !== null ) {
            setStatusFilter(filter)
        }
        setShowOnlyFavourites(JSON.parse(localStorage.getItem(localStorageShowOnlyFavs)))
    }, [location.search])

    const handleStatusChange = (event) => {
        const value = event.target.value
        localStorage.setItem(localStorageStatusesKey, value)
        setStatusFilter( value )
    }

    const addFavouriteProject = (event, projectID) => {
        event.preventDefault()
        userAddFavouriteProject(currentUser, projectID)
        table.current.dt().rows().every( (index) => {
            const row = table.current.dt().row(index)
            const data = row.data()
            if( data.id === projectID ) {
                data.is_favourite = true
                row.data(data)
            }
            return true
        })
    }
    
    const removeFavouriteProject = (event, projectID) => {
        event.preventDefault()
        userRemoveFavouriteProject(currentUser, projectID)
        table.current.dt().rows().every( (index) => {
            const row = table.current.dt().row(index)
            const data = row.data()
            if( data.id === projectID ) {
                data.is_favourite = false
                row.data(data)
            }
            return true
        })
    }

    const toggleShowOnlyFavourites = () => {
        localStorage.setItem(localStorageShowOnlyFavs, JSON.stringify(!showOnlyFavourites))
        setShowOnlyFavourites(!showOnlyFavourites)
    }

    const onlyFavouritesIcon = showOnlyFavourites ?
                                     (
                                         <div>
                                             <button type="button" className="btn btn-white btn-mr"
                                                     style={{marginBottom: "0px", paddingTop: "8px", paddingBottom: "7px", color: "#3a90d7"}}
                                                     data-tooltip-id="gnctooltip" data-tooltip-content="Show Favourites"
                                                     onClick={() => toggleShowOnlyFavourites() }
                                             >
                                                 <i className="fa fa-thumb-tack"></i>
                                             </button>
                                         </div>
                                     ) :
                                     (
                                         <div>
                                             <button type="button" className="btn btn-white btn-mr"
                                                     style={{marginBottom: "0px", paddingTop: "8px", paddingBottom: "8px"}}
                                                     data-tooltip-id="gnctooltip" data-tooltip-content="Show Favourites"
                                                    onClick={() => toggleShowOnlyFavourites() }
                                             >
                                                 <i className="fa fa-thumb-tack grey"></i>
                                             </button>
                                         </div>
                                     )

    if (!isUserManagementInitialized) {
        return <Loading />
    }
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            
                            {userCanCreateOpportunity(currentUser) && <NewOpportunityButton/> }
                            {userCanCreateProject(currentUser) && <NewProjectButton/> }
                            {userCanCreateProduct(currentUser) && <NewProductButton /> }

                            <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                                Select an Opportunity, Project or Product below to start work. <br/>
                                Further information on each is available through the Information menu item.
                            </div>
                        </div>
                        
                        <div className="ibox-content">
                            <div className="row">
                                <SearchBox tableRef={table} />
                                <div className="col-sm-1 m-b-xs" style={{textAlign: "right", paddingRight: "0px"}}>
                                    {onlyFavouritesIcon}
                                </div>
                                <StatusOptions
                                    className="stonly-portfolio-filter"
                                    options={statuses}
                                    value={statusFilter}
                                    onChange={(event) => handleStatusChange(event)}
                                />
                            </div>

                            <GncDataTable
                                key="project-portfolio-table"
                                tableRef={table}
                                data={visibleTableData}
                                defaultOrder={defaultOrder}
                                localStorageOrderKey={localStorageOrderKey}
                                columns={columns}
                                tableSlots={tableSlots}
                                tableHead={tableHead}
                                tableFoot={tableHead}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default ProjectPortfolio
